import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
const _excluded = ["user", "latitude", "longitude", "_created", "serverTimestamp"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Suggestion apply helpers, audit trail display, and restore entry building for TVC issues
 * fix flow.
 */

import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

/**
 * Inner audit log entries (nested `auditTrail.auditTrail`) that include a `value`.
 * Sorted by `_created` ascending.
 * @param {Object|null} auditTrailRoot - `rowData.auditTrail` (outer object)
 * @returns {Array<{ entryId: string, entry: Object }>}
 */
export const getAuditTrailRestorableEntries = auditTrailRoot => {
  const inner = auditTrailRoot === null || auditTrailRoot === void 0 ? void 0 : auditTrailRoot.auditTrail;
  if (!inner || typeof inner !== 'object' || Array.isArray(inner)) {
    return [];
  }
  const rows = Object.entries(inner).filter(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      log = _ref2[1];
    return log != null && typeof log === 'object' && !Array.isArray(log) && Object.prototype.hasOwnProperty.call(log, 'value') && log.value != null;
  }).map(_ref3 => {
    let _ref4 = _slicedToArray(_ref3, 2),
      entryId = _ref4[0],
      entry = _ref4[1];
    return {
      entryId,
      entry
    };
  });
  rows.sort((a, b) => {
    var _a$entry$_created, _b$entry$_created;
    /* eslint-disable no-underscore-dangle */
    const ta = new Date((_a$entry$_created = a.entry._created) !== null && _a$entry$_created !== void 0 ? _a$entry$_created : 0).getTime();
    const tb = new Date((_b$entry$_created = b.entry._created) !== null && _b$entry$_created !== void 0 ? _b$entry$_created : 0).getTime();
    /* eslint-enable no-underscore-dangle */
    const na = Number.isNaN(ta);
    const nb = Number.isNaN(tb);
    if (na && nb) return 0;
    if (na) return 1;
    if (nb) return -1;
    return ta - tb;
  });
  return rows;
};

/**
 * Human-readable value for a restorable audit entry (displayValue or nested value).
 * @param {Object} entry - Single inner auditTrail log entry
 * @returns {string}
 */
export const getAuditEntryPrimaryValue = entry => {
  if ((entry === null || entry === void 0 ? void 0 : entry.displayValue) != null && String(entry.displayValue).trim() !== '') {
    return String(entry.displayValue);
  }
  const raw = entry === null || entry === void 0 ? void 0 : entry.value;
  if (raw == null) return '—';
  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
    return String(raw);
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const first = Object.values(raw)[0];
    if (first && typeof first === 'object' && first.title != null) {
      return String(first.title);
    }
    try {
      return JSON.stringify(raw);
    } catch (_unused) {
      return '—';
    }
  }
  return String(raw);
};

/**
 * Short display string for audit `_created` ISO timestamps.
 * @param {string} iso - ISO date string
 * @returns {string}
 */
export const formatAuditCreated = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Display label for a suggestion option (primitive or { title, value, optionKey, … }).
 * @param {unknown} rawOption
 * @returns {string}
 */
export const normalizeSuggestionOptionLabel = rawOption => {
  if (rawOption == null) return '';
  if (typeof rawOption === 'object' && !Array.isArray(rawOption)) {
    var _ref5, _rawOption$title;
    const t = (_ref5 = (_rawOption$title = rawOption.title) !== null && _rawOption$title !== void 0 ? _rawOption$title : rawOption.value) !== null && _ref5 !== void 0 ? _ref5 : rawOption.label;
    return t != null ? String(t) : '';
  }
  return String(rawOption);
};

/** Matches option-map keys in content (e.g. grade / diameter pickers). */
const TVC_SUGGESTION_OPTION_KEY_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Error types whose content path expects a picker object `{ [uuid]: { title } }`, not a primitive.
 * Keep aligned with form JSON schema (e.g. `diameter.type === "object"`).
 */
const TVC_ISSUE_ERROR_TYPES_OBJECT_VALUE = new Set(['Missing Grade', 'Missing Diameter', 'Missing Coating', 'Missing Wall Thickness', 'Missing Weld Number', 'Duplicate Weld Number']);
export const tvcIssueErrorTypeUsesObjectValue = errorType => {
  const k = errorType == null ? '' : String(errorType).trim();
  return k !== '' && TVC_ISSUE_ERROR_TYPES_OBJECT_VALUE.has(k);
};

/**
 * Picker option id for `{ [optionKey]: { title } }`: spec, option row, or UUID-like suggestion key.
 * @param {string} typeKey
 * @param {Object} spec
 * @param {unknown} rawOption
 * @returns {string}
 */
export const resolveSuggestionOptionObjectKey = (typeKey, spec, rawOption) => {
  var _ref6, _spec$optionKey;
  const fromSpec = String((_ref6 = (_spec$optionKey = spec === null || spec === void 0 ? void 0 : spec.optionKey) !== null && _spec$optionKey !== void 0 ? _spec$optionKey : spec === null || spec === void 0 ? void 0 : spec.optionId) !== null && _ref6 !== void 0 ? _ref6 : '').trim();
  if (fromSpec) return fromSpec;
  if (rawOption != null && typeof rawOption === 'object' && !Array.isArray(rawOption)) {
    var _ref7, _rawOption$optionKey;
    const fromRow = String((_ref7 = (_rawOption$optionKey = rawOption.optionKey) !== null && _rawOption$optionKey !== void 0 ? _rawOption$optionKey : rawOption.optionId) !== null && _ref7 !== void 0 ? _ref7 : '').trim();
    if (fromRow) return fromRow;
  }
  const tk = typeof typeKey === 'string' ? typeKey.trim() : '';
  if (tk && TVC_SUGGESTION_OPTION_KEY_UUID_RE.test(tk)) {
    return tk;
  }
  return '';
};

/**
 * Value to merge at {@link resolveSuggestionContentPath} for applyMergePatches.
 * - string / number (default): primitive
 * - option (or errorType with object schema): `{ [optionKey]: { title } }`; if no option id is
 *   present in spec/option row/suggestion key, a new UUID is used so content stays object-shaped.
 *
 * @param {string|undefined|null} fieldType - 'string' | 'number' | 'option'
 * @param {string} typeKey - suggestions map key (UUID fallback for option id)
 * @param {Object} spec - suggestion spec (optionKey, optionId, …)
 * @param {unknown} rawOption - option row from spec.options
 * @param {string|undefined|null} [errorType] - issue row `errorType` (picker vs primitive)
 * @returns {string|number|Object|null}
 */
export const buildSuggestionContentValue = (fieldType, typeKey, spec, rawOption, errorType) => {
  const ft = String(fieldType !== null && fieldType !== void 0 ? fieldType : 'string').toLowerCase();
  const useObjectShape = ft === 'option' || tvcIssueErrorTypeUsesObjectValue(errorType);
  if (useObjectShape) {
    const label = normalizeSuggestionOptionLabel(rawOption).trim();
    if (!label) return null;
    const optionKey = resolveSuggestionOptionObjectKey(typeKey, spec, rawOption) || uuidv4();
    return {
      [optionKey]: {
        title: label
      }
    };
  }
  if (ft === 'number') {
    var _rawOption$value;
    const raw = typeof rawOption === 'object' && rawOption !== null && !Array.isArray(rawOption) ? (_rawOption$value = rawOption.value) !== null && _rawOption$value !== void 0 ? _rawOption$value : rawOption.title : rawOption;
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    return n;
  }
  return normalizeSuggestionOptionLabel(rawOption);
};

/**
 * TVC issue `errorType` → content path when suggestion spec omits `contentPath` / `path`.
 * Keep in sync with server `ERROR_TYPE_AUDIT_PATH`.
 */
const TVC_ISSUE_ERROR_TYPE_TO_CONTENT_PATH = {
  'Missing Heat Number': '/heatNumber',
  'Missing Asset Number': '/assetNumber',
  'Missing Grade': '/grade',
  'Missing Wall Thickness': '/wallThickness',
  'Missing Diameter': '/diameter',
  'Missing Coating': '/coating',
  'Duplicate Asset Number': '/assetNumber',
  'Missing Weld Number': '/title',
  'Missing Barcode': '/barcode',
  'Duplicate Barcode': '/barcode',
  'Duplicate Weld Number': '/title',
  Damaged: '/damaged',
  'Missing Weld Location': '/highAccuracyLocation',
  'Missing Mill Length': '/millLength'
};

/**
 * Content path for a suggestion fix. Prefers spec.contentPath / spec.path; else path-like
 * typeKey; else `errorType` map (aligned with audit breadcrumbs).
 * @param {string} typeKey
 * @param {Object} spec
 * @param {string|undefined|null} [errorType] - issue row `errorType`
 * @returns {string|null}
 */
export const resolveSuggestionContentPath = (typeKey, spec, errorType) => {
  var _spec$contentPath;
  const fromSpec = (_spec$contentPath = spec === null || spec === void 0 ? void 0 : spec.contentPath) !== null && _spec$contentPath !== void 0 ? _spec$contentPath : spec === null || spec === void 0 ? void 0 : spec.path;
  if (typeof fromSpec === 'string' && fromSpec.trim() !== '') {
    return fromSpec.trim();
  }
  if (typeof typeKey === 'string') {
    const tk = typeKey.trim();
    if (tk.startsWith('/') && tk.length > 1) {
      return tk;
    }
  }
  const ek = errorType == null ? '' : String(errorType).trim();
  if (ek && TVC_ISSUE_ERROR_TYPE_TO_CONTENT_PATH[ek]) {
    return TVC_ISSUE_ERROR_TYPE_TO_CONTENT_PATH[ek];
  }
  return null;
};

/**
 * TVC issue `errorType` → audit trail `breadcrumbs` when the suggestion spec omits them.
 */
const TVC_ISSUE_ERROR_TYPE_TO_BREADCRUMBS = {
  'Missing Heat Number': 'General > Heat Number',
  'Missing Asset Number': 'General > Asset Number',
  'Missing Grade': 'Pipe > Grade',
  'Missing Wall Thickness': 'Pipe > Wall Thickness',
  'Missing Diameter': 'Pipe > Diameter',
  'Missing Coating': 'Pipe > Coating',
  'Duplicate Asset Number': 'General > Asset Number',
  'Missing Weld Number': 'General > Weld Number',
  'Missing Barcode': 'General > Barcode',
  'Duplicate Barcode': 'General > Barcode',
  'Duplicate Weld Number': 'General > Weld Number',
  Damaged: 'Pipe > Damaged',
  'Missing Mill Length': 'Pipe > Mill Length'
};

/**
 * Breadcrumbs for a suggestion-driven audit log row. Spec wins when present.
 * @param {string|undefined|null} errorType - issue row `errorType`
 * @param {string|undefined|null} specBreadcrumbs - optional `spec.breadcrumbs`
 * @returns {string}
 */
export const resolveTvcSuggestionAuditBreadcrumbs = (errorType, specBreadcrumbs) => {
  var _TVC_ISSUE_ERROR_TYPE;
  if (typeof specBreadcrumbs === 'string' && specBreadcrumbs.trim() !== '') {
    return specBreadcrumbs.trim();
  }
  const k = errorType == null ? '' : String(errorType).trim();
  if (!k) return '';
  return (_TVC_ISSUE_ERROR_TYPE = TVC_ISSUE_ERROR_TYPE_TO_BREADCRUMBS[k]) !== null && _TVC_ISSUE_ERROR_TYPE !== void 0 ? _TVC_ISSUE_ERROR_TYPE : '';
};

/**
 * New audit log row for a restore: same fields as the source log entry except
 * `user`, `latitude`, `longitude`, `_created`, and `serverTimestamp` (timestamp fields
 * use the same new ISO instant: `serverTimestamp` matches `_created`).
 * Matches the shape produced elsewhere (e.g. {@link ../commonActions/logValue}).
 *
 * @param {Object} sourceEntry - Historical inner `auditTrail` map value
 * @param {string} userFullName - Current user display name
 * @param {number|undefined|null} latitude - Session geolocation if available
 * @param {number|undefined|null} longitude - Session geolocation if available
 * @returns {Object}
 */
export const buildRestoredAuditLogEntry = (sourceEntry, userFullName, latitude, longitude) => {
  var _sourceEntry$path, _ref8, _sourceEntry$contentP;
  if (sourceEntry == null || typeof sourceEntry !== 'object') {
    return null;
  }
  /* eslint-disable no-underscore-dangle */
  const _u = sourceEntry.user,
    _la = sourceEntry.latitude,
    _lo = sourceEntry.longitude,
    _c = sourceEntry._created,
    _serverTs = sourceEntry.serverTimestamp,
    rest = _objectWithoutProperties(sourceEntry, _excluded);
  /* eslint-enable no-underscore-dangle */
  const path = (_sourceEntry$path = sourceEntry.path) !== null && _sourceEntry$path !== void 0 ? _sourceEntry$path : sourceEntry.contentPath;
  const createdIso = moment().toISOString();
  const next = _objectSpread(_objectSpread({}, rest), {}, {
    _created: createdIso,
    serverTimestamp: createdIso,
    user: userFullName && String(userFullName).trim() || rest.user || '',
    path,
    contentPath: (_ref8 = (_sourceEntry$contentP = sourceEntry.contentPath) !== null && _sourceEntry$contentP !== void 0 ? _sourceEntry$contentP : sourceEntry.path) !== null && _ref8 !== void 0 ? _ref8 : path
  });
  if (latitude != null && !Number.isNaN(Number(latitude))) {
    next.latitude = Number(latitude);
  }
  if (longitude != null && !Number.isNaN(Number(longitude))) {
    next.longitude = Number(longitude);
  }
  return next;
};